import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Hash, Shield, Save, DownloadCloud, Loader2, FileDown, FileUp, Archive, RotateCcw, Trash2, FileSpreadsheet, RefreshCw } from "lucide-react";
import { BotStatusButton } from "@/components/BotStatus";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiError } from "@/lib/api";

// Header condiviso: tab Canali/Ruoli + toolbar (backup, import/export, sync) + dialog.
export function StructureHeader({ s, tab }) {
  const navigate = useNavigate();
  const [backupOpen, setBackupOpen] = useState(false);
  const [backupList, setBackupList] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);
  const { roles: liveRoles, guild } = useData();

  const exportExcel = async () => {
    setExcelBusy(true);
    try {
      const { exportRolesExcel } = await import("@/lib/excelExport");
      await exportRolesExcel({
        roles: s.roles, categories: s.categories, loose: s.loose, permGroups: s.meta?.permissions || [],
        memberCounts: Object.fromEntries(liveRoles.map((r) => [r.id, r.memberCount])), guildName: guild?.name || "Server",
      });
      toast.success("File Excel creato", { description: s.dirty ? "Include anche le modifiche non ancora salvate." : "Ruoli e permessi attuali del server." });
    } catch (e) { toast.error("Creazione del file non riuscita", { description: e.message }); } finally { setExcelBusy(false); }
  };

  const channelCount = s.categories.reduce((n, c) => n + c.channels.length, 0) + s.loose.length + s.categories.length;
  const roleCount = s.roles.length;

  const loadBackups = async () => { try { setBackupList(await s.backupsApi.list()); } catch (e) { toast.error(apiError(e)); } };
  const openBackups = () => { setBackupOpen(true); loadBackups(); };
  const createBackup = async () => {
    setBackupBusy(true);
    try { await s.backupsApi.create(); toast.success("Backup creato"); await loadBackups(); }
    catch (e) { toast.error(apiError(e)); } finally { setBackupBusy(false); }
  };
  const restoreBackup = async (id) => {
    try {
      const b = await s.backupsApi.get(id); const snap = b.snapshot || {};
      s.build(snap.roles || [], snap.channels || []); setBackupOpen(false);
      toast.success("Backup caricato nell'editor — premi «Salva» per applicarlo");
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteBackup = async (id) => {
    try { await s.backupsApi.remove(id); await loadBackups(); toast.success("Backup eliminato"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const TabBtn = ({ id, to, icon: Icon, label, count }) => (
    <button data-testid={`structure-tab-${id}`} onClick={() => tab !== id && navigate(to)}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition-all ${
        tab === id ? "bg-[#5865F2] text-white shadow-[0_0_18px_rgba(88,101,242,0.3)]" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
      <Icon className="h-4 w-4" />{label} <span className="opacity-70">({count})</span>
    </button>
  );

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-xl bg-[#12141D] p-1">
          <TabBtn id="canali" to="/canali" icon={Hash} label="Canali" count={channelCount} />
          <TabBtn id="ruoli" to="/ruoli" icon={Shield} label="Ruoli" count={roleCount} />
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={s.fileRef} type="file" accept="application/json,.json" onChange={s.importFile} className="hidden" data-testid="backup-file-input" />
          <BotStatusButton />
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={excelBusy} data-testid="excel-export-btn" title="Scarica un file Excel con ruoli e permessi" className="border-[#1D6F42]/60 bg-[#1D6F42]/15 text-[#8fe3b0] hover:bg-[#1D6F42]/30 hover:text-white">{excelBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}Excel</Button>
          <Button variant="outline" size="sm" onClick={s.exportBackup} data-testid="backup-export-btn" className="border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D]"><FileDown className="mr-2 h-4 w-4" />Esporta</Button>
          <Button variant="outline" size="sm" onClick={() => s.fileRef.current?.click()} data-testid="backup-import-btn" className="border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D]"><FileUp className="mr-2 h-4 w-4" />Importa</Button>
          <Button variant="outline" size="sm" onClick={openBackups} data-testid="backups-open-btn" className="border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D]"><Archive className="mr-2 h-4 w-4" />Backup</Button>
          <Button variant="outline" size="sm" onClick={s.importSnapshot} disabled={s.importing} title="Ricarica ruoli e canali così come sono ora su Discord (annulla le modifiche non salvate)" data-testid="import-snapshot-btn" className="border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D]">{s.importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DownloadCloud className="mr-2 h-4 w-4" />}Ricarica</Button>
          <Button size="sm" onClick={s.save} disabled={s.saving || !s.dirty} data-testid="structure-save-btn" className="bg-[#5865F2] font-semibold hover:bg-[#4752C4]">{s.saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salva</Button>
        </div>
      </div>

      {s.remoteChanged && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#00F0FF]/30 bg-[#00F0FF]/[0.08] px-4 py-3 text-sm" data-testid="remote-change-banner">
          <RefreshCw className="h-4 w-4 shrink-0 text-[#00F0FF]" />
          <span className="flex-1 text-[#C9CDD4]"><b className="text-white">Qualcuno ha modificato il server su Discord.</b> Hai modifiche non salvate: salvale oppure carica la versione aggiornata (le tue modifiche andranno perse).</span>
          <Button size="sm" onClick={s.discard} className="bg-[#00F0FF]/20 text-[#7fe9f5] hover:bg-[#00F0FF]/30">Carica da Discord</Button>
        </div>
      )}

      {/* Backup dialog */}
      <Dialog open={backupOpen} onOpenChange={setBackupOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">Backup salvati</DialogTitle></DialogHeader>
          <p className="text-sm text-[#949BA4]">Copie giornaliere automatiche (03:00) e manuali. Ripristina per caricarne una nell'editor, poi premi «Salva».</p>
          <Button onClick={createBackup} disabled={backupBusy} data-testid="backup-create-now-btn" className="w-fit bg-[#5865F2] hover:bg-[#4752C4]">{backupBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Archive className="mr-2 h-4 w-4" />}Crea backup ora</Button>
          <div className="space-y-2" data-testid="backups-list">
            {backupList.length === 0 && <p className="text-sm text-[#949BA4]">Nessun backup ancora.</p>}
            {backupList.map((b) => (
              <div key={b.id} className="flex items-center gap-3 rounded-lg bg-[#131620] px-3 py-2.5">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${b.auto ? "bg-[#5865F2] text-white" : "bg-[#232838] text-[#B5BAC1]"}`}>{b.auto ? "AUTO" : "MANUALE"}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">{b.at ? new Date(b.at).toLocaleString("it-IT") : ""}</p>
                  <p className="text-xs text-[#949BA4]">{b.roleCount} ruoli · {b.channelCount} canali</p>
                </div>
                <Button size="sm" onClick={() => restoreBackup(b.id)} data-testid={`backup-restore-${b.id}`} className="bg-[#248046] hover:bg-[#1a6334]"><RotateCcw className="mr-1 h-4 w-4" />Ripristina</Button>
                <Button size="icon" variant="ghost" onClick={() => deleteBackup(b.id)} className="text-[#F23F43] hover:bg-[#F23F43]/15"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Barra modifiche non salvate */}
      {s.dirty && createPortal(
        <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 lg:pl-[272px]" data-testid="structure-unsaved-bar">
          <div className="glass flex w-full max-w-2xl items-center gap-3 rounded-2xl border border-[#5865F2]/40 bg-[#161923]/95 px-4 py-3 shadow-[0_10px_40px_-10px_rgba(88,101,242,0.5)] animate-fade-up">
            <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-[#FEE75C]" />
            <span className="flex-1 text-sm font-medium text-white">Hai modifiche non salvate</span>
            <Button size="sm" variant="ghost" onClick={s.discard} disabled={s.saving} className="text-[#B5BAC1] hover:bg-white/5 hover:text-white" data-testid="structure-discard-btn">Annulla</Button>
            <Button size="sm" onClick={s.save} disabled={s.saving} className="bg-[#23A55A] font-semibold hover:bg-[#1f8f4e]" data-testid="structure-bar-save-btn">{s.saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salva su Discord</Button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
